import { useEffect } from 'react'
import { useSitePublic } from '@/hooks/data/useSite'

/** Curated real Google Fonts an admin can pick — see GOOGLE_FONTS in the Theme Settings page. */
const GOOGLE_FONT_CSS_NAME: Record<string, string> = {
  Outfit: 'Outfit:wght@400;500;600;700',
  Inter: 'Inter:wght@400;500;600;700',
  Poppins: 'Poppins:wght@400;500;600;700',
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@400;500;600;700',
  Manrope: 'Manrope:wght@400;500;600;700',
  'DM Sans': 'DM+Sans:wght@400;500;600;700',
  Lato: 'Lato:wght@400;700',
  'Work Sans': 'Work+Sans:wght@400;500;600;700',
}

const FONT_LINK_ID = 'brand-google-font'
const FAVICON_LINK_ID = 'brand-favicon'

/**
 * Admin-dashboard equivalent of ecommerce-app's ThemeApplier. Distinct from
 * `ThemeContext`'s `ThemeProvider`, which only toggles light/dark mode — this
 * applies the real, admin-configured brand theme (Site.PrimaryColor/FontFamily/
 * FaviconUrl) to the live DOM. Renders nothing.
 */
export function ThemeApplier() {
  const { data } = useSitePublic()
  const site = data?.site

  useEffect(() => {
    if (!site) return
    const root = document.documentElement

    // removeProperty in the falsy branch matters: without it, "Reset to default" (which sends
    // an empty string) would leave whatever color a PREVIOUS setProperty call applied stuck in
    // the live DOM — the effect re-runs with the new (empty) value but never un-sets the old one.
    if (site.primaryColor) root.style.setProperty('--brand-accent', site.primaryColor)
    else root.style.removeProperty('--brand-accent')

    if (site.fontFamily && GOOGLE_FONT_CSS_NAME[site.fontFamily]) {
      let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.id = FONT_LINK_ID
        link.rel = 'stylesheet'
        document.head.appendChild(link)
      }
      link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_CSS_NAME[site.fontFamily]}&display=swap`
      // body applies the `font-outfit` utility directly (Tailwind v4 `--font-*` namespace),
      // so overriding this variable — not a generic `--font-sans` — is what actually re-tints it.
      root.style.setProperty('--font-outfit', `'${site.fontFamily}', sans-serif`)
    } else {
      root.style.removeProperty('--font-outfit')
    }

    if (site.faviconUrl) {
      let icon = document.getElementById(FAVICON_LINK_ID) as HTMLLinkElement | null
      if (!icon) {
        icon = document.createElement('link')
        icon.id = FAVICON_LINK_ID
        icon.rel = 'icon'
        document.head.appendChild(icon)
      }
      icon.href = site.faviconUrl
    } else {
      // Removing the override link (rather than leaving a stale href) lets whatever static
      // favicon is declared in the document head show through again.
      document.getElementById(FAVICON_LINK_ID)?.remove()
    }
  }, [site])

  return null
}
